import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { lastValueFrom, Subscription } from "rxjs";
import { SearchForm, SearchFormOptionItem } from "../../../Model/Dto/SearchForm";
import { SearchFormsService } from "../../../Services/SearchForms/searchforms.service";
import { LoginService } from "../../../Services/login.service";
import { SignalrService } from "../../../Services/signalr.service";
import { TemplateFieldsService } from "../../../Services/templateFields.service";
import { ValidFeatureService } from "../../../Services/validFeature.service";
import { WawiListService } from "../../../Services/wawiList.service";
import { CatalogService } from "../../../Services/CatalogManagement/catalog.service";
import { InputEvent as TagBoxInputEvent } from "devextreme/ui/tag_box";

@Component({
    selector: "searchFormOptions",
    templateUrl: "searchFormOptions.component.html",
    styleUrls: [],
    standalone: false
})
export class SearchFormOptionsComponent implements OnInit, OnDestroy {

  private _refreshWithSearchForm: SearchForm;
  private initialized = false;
  private dropdownValuesCache = new Map<number, string[]>();
  private jobFinishedSub: Subscription;

  @Input() set refreshWithSearchForm(value: SearchForm) {
    this._refreshWithSearchForm = value;
    if (this.initialized) {
      this.applyRefresh(value);
    }
  }
  get refreshWithSearchForm(): SearchForm {
    return this._refreshWithSearchForm;
  }

  @Input() catalogId: string;
  @Input() syncWithService = true;
  @Output() onEditSearchForm = new EventEmitter<SearchForm>();
  @Output() onExecuteSearchForm = new EventEmitter<SearchForm>();

  allSearchForms: SearchForm[] = [];
  selectedSearchForm: SearchForm;

  private noValueOperators = new Set(['is_empty', 'is_not_empty', 'is_true', 'is_false']);
  private rangeOperators = new Set(['between', 'not_between']);
  private multiValueOperators = new Set(['equals_not', 'one_of', 'none_of']);

  private searchTimeout: any;

  constructor(
    public translate: TranslateService,
    private searchFormsService: SearchFormsService,
    public loginService: LoginService,
    private templateFieldsService: TemplateFieldsService,
    private validFeatureService: ValidFeatureService,
    private wawiListService: WawiListService,
    private catalogService: CatalogService,
    private signalrService: SignalrService
  ) {}

  async ngOnInit(): Promise<void> {
    this.jobFinishedSub = this.signalrService.ShowJobFinishedNotification$.subscribe(info => {
      if (info.jobType === 'CreateSearchFormIndex') {
        this.loadSearchForms();
      }
    });

    await this.loadSearchForms();
    this.applyActiveSearchForm();
    if (!this.selectedSearchForm && this._refreshWithSearchForm?.id) {
      const foundForm = this.allSearchForms.find(sf => sf.id === this._refreshWithSearchForm.id) ?? null;
      if (foundForm && !(foundForm as any).disabled) {
        if (this._refreshWithSearchForm.searchOptions?.length > 0) {
          foundForm.searchOptions = this._refreshWithSearchForm.searchOptions;
        }
        this.selectedSearchForm = foundForm;
      }
      if (this.syncWithService) {
        this.searchFormsService.activeSearchForm = this.selectedSearchForm ?? null;
      }
    }
    await this.loadDropdownValues();
    this.initialized = true;
  }

  private async applyRefresh(searchForm: SearchForm) {
    await this.loadSearchForms();
    if (searchForm) {
      const foundForm = this.allSearchForms.find(sf => sf.id === searchForm.id) ?? null;
      if (foundForm && !(foundForm as any).disabled) {
        if (searchForm.searchOptions?.length > 0) {
          foundForm.searchOptions = searchForm.searchOptions;
        }
        this.selectedSearchForm = foundForm;
      } else {
        this.selectedSearchForm = null;
      }
    } else {
      this.selectedSearchForm = null;
    }
    if (this.syncWithService) {
      this.searchFormsService.activeSearchForm = this.selectedSearchForm ?? null;
    }
    await this.loadDropdownValues();
    this.onExecuteSearchForm.emit(this.selectedSearchForm ?? null);
  }

  async loadSearchForms() {
    const customerId = this.loginService.currentUser.customerId;
    this.allSearchForms = await this.searchFormsService.getAll(customerId) ?? [];
    this.allSearchForms.forEach(sf => {
      (sf as any).disabled = sf.indexState !== 'ready_for_usage';
    });
    this.applyActiveSearchForm();
  }

  private applyActiveSearchForm() {
    if (!this.syncWithService)
      return;
    const active = this.searchFormsService.activeSearchForm;
    if (active && !this.selectedSearchForm) {
      this.selectedSearchForm = this.allSearchForms.find(sf => sf.id === active.id) ?? null;
    }
    if (!active) {
      this.selectedSearchForm = null;
    }
  }

  async onSearchFormSelectionChanged() {
    await this.loadDropdownValues();
    await this.executeSearchForm();
  }

  onTriggerAutomaticSearchAfterTimeout() {
    
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
    this.searchTimeout = setTimeout(() => {
      this.executeSearchForm();
    }, 500);    
  }

  async executeSearchForm() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
    
    if (this.syncWithService) {
      this.searchFormsService.activeSearchForm = this.selectedSearchForm ?? null;
    }
    this.onExecuteSearchForm.emit(this.selectedSearchForm ?? null);
  }

  ngOnDestroy(): void {
    this.jobFinishedSub?.unsubscribe();
    
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }

  editSearchForm() {
    this.onEditSearchForm.emit(this.selectedSearchForm);
  }

  private async loadDropdownValues(): Promise<void> {
    this.dropdownValuesCache.clear();
    if (!this.selectedSearchForm?.searchOptions?.length) return;

    const customerId = this.loginService.currentUser.customerId;
    const catalogId = this.catalogId || this.catalogService.selectedCatalogId;

    const promises = this.selectedSearchForm.searchOptions.map(async (row, index) => {
      const dataType = this.getDataType(row);
      if (dataType !== 'string' && dataType !== 'number') return;

      const elementKey = row.nextPimField?.elementKey;
      const field = row.nextPimField?.field;

      try {
        if (elementKey) {
          // Feature-Feld: vordefinierte Merkmalswerte laden
          const values = await lastValueFrom(this.validFeatureService.getValues(elementKey, customerId));
          const stringValues = (values ?? []).map(v => v.value).filter(v => !!v);
          if (stringValues.length) {
            this.dropdownValuesCache.set(index, stringValues);
          }
        } else if (field && catalogId) {
          // Standard-Feld: Distinct-Werte aus dem Katalog laden
          const values = await this.wawiListService.getCatalogFieldValues(catalogId, customerId, [field], 500);
          const filtered = (values ?? []).filter(v => !!v);
          if (filtered.length) {
            this.dropdownValuesCache.set(index, filtered);
          }
        }
      } catch {
        // Einzelne Felder still ignorieren
      }
    });

    await Promise.all(promises);
  }

  getDropdownValues(index: number): string[] {
    return this.dropdownValuesCache.get(index) ?? [];
  }

  getFieldDisplayText(row: SearchFormOptionItem): string {
    const fieldName = row.nextPimField?.field;
    if (!fieldName)
      return '';
    const field = this.templateFieldsService.translatedFields?.find(f => f.field === fieldName);
    if (!field || field.text === field.field)
      return fieldName;
    return field.text;
  }

  isNoValue(row: SearchFormOptionItem): boolean {
    return this.noValueOperators.has(row.operator);
  }

  isRange(row: SearchFormOptionItem): boolean {
    return this.rangeOperators.has(row.operator);
  }

  isMultiValue(row: SearchFormOptionItem): boolean {
    return this.multiValueOperators.has(row.operator);
  }

  getDataType(row: SearchFormOptionItem): string {
    if (row?.nextPimField?.field === 'FVALUE' && row?.dataType)
      return row.dataType;

    const fieldName = row.nextPimField?.field;
    if (!fieldName)
      return 'string';
    const field = this.templateFieldsService.translatedFields?.find(f => f.field === fieldName);
    return field?.dataType ?? 'string';
  }

}
